import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { FiUser, FiLock, FiCheckCircle, FiSend, FiTrendingUp, FiTarget, FiActivity, FiLayers } from 'react-icons/fi';
import { RadialBarChart, RadialBar, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, ComposedChart, Line } from 'recharts';

const THEME = {
  primary: '#00B4D8', secondary: '#48CAE4', accent: '#90E0EF',
  success: '#10B981', warning: '#F59E0B', danger: '#EF4444',
  dark: '#0A1628', muted: '#64748B', purple: '#8B5CF6', pink: '#EC4899'
};

/* ── Animated Number ── */
const AnimatedNumber = ({ value, suffix = '', duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const numVal = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : (value || 0);
  useEffect(() => {
    if (isNaN(numVal)) { setDisplay(value); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * numVal));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [numVal, duration, value]);
  return <>{typeof value === 'string' && value.includes('%') ? `${display}%` : display.toLocaleString()}{suffix}</>;
};

/* ── Profile Avatar ── */
const ProfileAvatar = ({ username, size = 120 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', position: 'relative',
    background: `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.purple} 100%)`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.4, fontWeight: 800, color: '#fff',
    boxShadow: '0 12px 40px rgba(0,180,216,0.35)',
    animation: 'float 3s ease-in-out infinite'
  }}>
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
      }
    `}</style>
    {username?.charAt(0).toUpperCase()}
    <div style={{
      position: 'absolute', bottom: 4, right: 4, width: 32, height: 32, borderRadius: '50%',
      background: THEME.success, border: '3px solid var(--bg-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}><FiCheckCircle size={14} color="#fff" /></div>
  </div>
);

/* ── Stat Ring ── */
const StatRing = ({ value, max, label, color, icon }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const data = [{ value: percentage, fill: color }];

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ position: 'relative', width: 100, height: 100, margin: '0 auto' }}>
        <ResponsiveContainer>
          <RadialBarChart cx="50%" cy="50%" innerRadius="70%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" cornerRadius={10} background={{ fill: `${color}20` }} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          color, fontSize: 24
        }}>{icon}</div>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: THEME.dark, marginTop: 8 }}>
        <AnimatedNumber value={value} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: THEME.muted, textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
};



/* ── Activity Sparkline ── */
const ActivitySparkline = ({ data }) => {
  const sparkData = data || [
    { day: 'Mon', value: 3 }, { day: 'Tue', value: 7 }, { day: 'Wed', value: 2 },
    { day: 'Thu', value: 5 }, { day: 'Fri', value: 8 }, { day: 'Sat', value: 4 }, { day: 'Sun', value: 6 }
  ];

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiActivity size={18} color={THEME.success} /> Weekly Activity
      </h3>
      <ResponsiveContainer width="100%" height={100}>
        <ComposedChart data={sparkData}>
          <defs>
            <linearGradient id="sparkGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={THEME.primary} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={THEME.primary} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis dataKey="day" tick={{ fontSize: 10, fill: THEME.muted }} axisLine={false} tickLine={false} />
          <Area type="monotone" dataKey="value" fill="url(#sparkGradient)" stroke="none" />
          <Line type="monotone" dataKey="value" stroke={THEME.primary} strokeWidth={3} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Performance Donut ── */
const PerformanceDonut = ({ stats }) => {
  const data = [
    { name: 'Sent', value: stats?.totalSent || 0, color: THEME.success },
    { name: 'Failed', value: stats?.totalFailed || 0, color: THEME.danger },
    { name: 'Pending', value: (stats?.totalAssigned || 0) - (stats?.totalSent || 0) - (stats?.totalFailed || 0), color: THEME.warning }
  ].filter(d => d.value > 0);

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div style={{
      background: 'var(--bg-primary)', borderRadius: 16, padding: 24,
      boxShadow: '0 4px 16px rgba(0,0,0,0.06)', height: '100%'
    }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: THEME.dark, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <FiTarget size={18} color={THEME.purple} /> Message Stats
      </h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ width: 140, height: 140, position: 'relative' }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.length > 0 ? data : [{ value: 1, color: 'rgba(0,0,0,0.05)' }]}
                cx="50%" cy="50%" innerRadius={45} outerRadius={65} dataKey="value" strokeWidth={0}>
                {data.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: THEME.dark }}>{total.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: THEME.muted }}>TOTAL</div>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          {data.map((d, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 0', borderBottom: i < data.length - 1 ? '1px solid var(--border-color)' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: THEME.dark }}>{d.name}</span>
              </div>
              <span style={{ fontSize: 16, fontWeight: 800, color: d.color }}>{d.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};



export default function UserProfile() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/user/profile-stats').then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{
              width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #00B4D8 0%, #8B5CF6 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
            }}><FiUser size={22} /></span>
            My Profile
          </h1>
          <p className="page-subtitle">Manage your profile and preferences</p>
        </div>
      </div>

      {/* Profile Hero Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0,180,216,0.08) 0%, rgba(139,92,246,0.08) 100%)',
        borderRadius: 20, padding: 32, marginBottom: 24,
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 32, alignItems: 'center',
        border: '1px solid rgba(0,180,216,0.15)', animation: 'slideUp 0.5s ease both'
      }}>
        <ProfileAvatar username={user?.username} size={110} />
        
        <div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: THEME.dark, marginBottom: 4 }}>{user?.username}</h2>
          <p style={{ fontSize: 14, color: THEME.muted, marginBottom: 12 }}>{user?.email || 'No email set'}</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <span style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: `${THEME.primary}15`, color: THEME.primary, textTransform: 'uppercase'
            }}>{user?.role}</span>
            {stats && (
              <span style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                background: `${THEME.success}15`, color: THEME.success
              }}>{stats.successRate}% Success</span>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div style={{ display: 'flex', gap: 24 }}>
            <StatRing value={stats.totalCampaigns || 0} max={20} label="Campaigns" color={THEME.primary} icon={<FiLayers size={18} />} />
            <StatRing value={stats.totalSent || 0} max={10000} label="Sent" color={THEME.success} icon={<FiSend size={18} />} />
            <StatRing value={stats.successRate || 0} max={100} label="Success" color={THEME.purple} icon={<FiTrendingUp size={18} />} />
          </div>
        )}
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 20, marginBottom: 24 }}>
        <PerformanceDonut stats={stats} />
        <ActivitySparkline />
      </div>

      {/* Forms Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Account Info */}
        <div style={{
          background: 'var(--bg-primary)', borderRadius: 16, padding: 32,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)', animation: 'slideUp 0.5s ease 0.2s both',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', marginBottom: 20,
            background: `linear-gradient(135deg, ${THEME.primary} 0%, ${THEME.purple} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 8px 24px ${THEME.primary}30`
          }}>
            <FiUser size={36} color="#fff" />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: THEME.dark, marginBottom: 8 }}>
            {user?.username || 'User'}
          </h3>
          <span style={{
            padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: `${THEME.primary}15`, color: THEME.primary, textTransform: 'uppercase'
          }}>{user?.role || 'User'}</span>
        </div>

        {/* Change Password */}
        <div style={{
          background: 'var(--bg-primary)', borderRadius: 16, padding: 32,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)', animation: 'slideUp 0.5s ease 0.25s both',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', marginBottom: 20,
            background: `${THEME.purple}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FiLock size={36} color={THEME.purple} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: THEME.dark, marginBottom: 8 }}>
            Change Password
          </h3>
          <p style={{ 
            textAlign: 'center', color: THEME.muted, fontSize: 14, lineHeight: 1.6, margin: 0
          }}>
            Contact your <strong style={{ color: THEME.dark }}>Administrator</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
